/**
 * A collection of utility methods related to manipulating local storage.
 */

import { isNullable } from "./null-checks";

/**
 * Given a key-value pair, bind value to the key in local storage.
 * If the key is previously associated with another value, that value
 * will be overwritten. Values written can be retrieved with the same `key`
 * using `getFromLocalStorage`.
 *
 * @param key
 *  a string that will be used to retrieve the value in the future.
 * @param val
 *  value associated with the key. Supports literals such as strings and
 *  numbers, as well as objects.
 */
export const saveToLocalStorage = (key: string, val: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch (error) {
    // An error may happen when local storage is full.
    // See: https://developer.mozilla.org/en-US/docs/Web/API/Storage/setItem
    console.error(error);
  }
};

/**
 * Attempts to retrieve values from local storage using the provided key.
 * If the return value is `undefined`, it could mean that there is no value
 * under the provided key, or an exception occurred during retrieval.
 *
 * @param key the string used to retrieve value in local storage
 */
export const getFromLocalStorage = (key: string) => {
  try {
    const got = localStorage.getItem(key);
    return isNullable(got) ? undefined : JSON.parse(got);
  } catch (error) {
    console.error(error);
    return undefined;
  }
};
